import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { TempAndPersistedAvatarState } from '../../components/v2BaseComponents/ImagePicker/TempImagePicker';
import { useClearInMemoryAvatarCache } from '../useAvatar';
import { appAPI } from '../../utils/api';
import { getMedicalProfileDetailsCacheKey, onProfileContactDetailsUpdate } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('useMedicalProfile', {
    errorLoadingProfile: 'Error loading profile',
    errorUpdatingContactDetails: 'Error occurred while updating Medical Profile',
    updateContactDetailsSuccess: 'Medical profile updated successfully',
    createEmergencyContactSuccess: 'Emergency contact created successfully',
    createEmergencyContactError: 'Error occurred while creating emergency contact',
    deleteEmergencyContactSuccess: 'Emergency contact deleted successfully',
    deleteEmergencyContactError: 'Error occurred while deleting emergency contact',
    updateAvatarError: 'Error occurred while updating profile photo',
});

export type UseMedicalProfileArgs = {
    medicalProfileId: string | null;
    onContactDetailsUpdateSuccess?: () => void;
    onCreateEmergencyContactSuccess?: () => void;
    onDeleteEmergencyContactSuccess?: () => void;
};

export type UpdateContactDetailsArgs = {
    contactDetailsId: string;
    contactDetails: APITypes.medicalProfileService.UpdateContactDetailsBody;
};

export type CreateEmergencyContactArgs = {
    emergencyContactDetails: APITypes.medicalProfileService.CreateEmergencyContactDetailsBody;
    medicalProfileId: string;
};

export type DeleteEmergencyContactArgs = {
    medicalProfileId: string;
    emergencyContactId: string;
};

export type ProfileAvatarUpdateArgs = {
    avatarState: TempAndPersistedAvatarState;
};

export type UseMedicalProfileReturnValue = {
    profile: APITypes.medicalProfileService.GetProfileResponse | null;
    isLoadingProfile: boolean;
    updateContact: (args: UpdateContactDetailsArgs) => void;
    isUpdatingContact: boolean;
    createEmergencyContact: (args: CreateEmergencyContactArgs) => void;
    isCreatingEmergencyContact: boolean;
    deleteEmergencyContact: (args: DeleteEmergencyContactArgs) => void;
    isDeletingEmergencyContact: boolean;
};

export default function useMedicalProfile({
    medicalProfileId,
    onContactDetailsUpdateSuccess,
    onCreateEmergencyContactSuccess,
}: UseMedicalProfileArgs): UseMedicalProfileReturnValue {
    const clearInMemoryAvatarCache = useClearInMemoryAvatarCache();
    const notify = useNotify();
    const profileQuery = useQuery({
        queryKey: getMedicalProfileDetailsCacheKey(medicalProfileId || ''),
        queryFn: async () => {
            if (!medicalProfileId) throw new Error('Cannot get profile without a medical profile id');
            return await appAPI.get(urls.api.medicalProfiles.medicalProfile(medicalProfileId));
        },
        enabled: !!medicalProfileId,
    });
    useQueryErrorNotify(profileQuery, intl('errorLoadingProfile'));

    const contactDetailsMutation = useMutation({
        mutationFn: async (args: UpdateContactDetailsArgs) => {
            if (!medicalProfileId) throw new Error('Cannot update contact details without a medical profile id');

            return await appAPI.put(urls.api.medicalProfiles.contactDetails(medicalProfileId, args.contactDetailsId), {
                body: args.contactDetails,
            });
        },
        onError: () => {
            notify('error', intl('errorUpdatingContactDetails'));
        },
        onSuccess: () => {
            notify('success', intl('updateContactDetailsSuccess'));
            onContactDetailsUpdateSuccess?.();

            if (medicalProfileId) {
                clearInMemoryAvatarCache(urls.api.medicalProfiles.avatar(medicalProfileId));
                onProfileContactDetailsUpdate.resetCache({ medicalProfileId })();
            }
        },
    });

    const deleteEmergencyContactMutation = useMutation({
        mutationFn: async (args: DeleteEmergencyContactArgs) => {
            if (!medicalProfileId) throw new Error('Cannot delete emergency contact without a medical profile id');

            await appAPI.del(urls.api.medicalProfiles.contactDetails(medicalProfileId, args.emergencyContactId));
        },
        onError: () => {
            notify('error', intl('deleteEmergencyContactError'));
        },
        onSuccess: () => {
            notify('success', intl('deleteEmergencyContactSuccess'));
            onContactDetailsUpdateSuccess?.();

            if (medicalProfileId) {
                onProfileContactDetailsUpdate.resetCache({ medicalProfileId })();
            }
        },
    });

    const createEmergencyContact = useMutation({
        mutationFn: async (args: CreateEmergencyContactArgs) => {
            if (!args.medicalProfileId) throw new Error('Cannot create emergency contact without a medical profile id');

            await appAPI.post(urls.api.medicalProfiles.emergencyContact(args.medicalProfileId), {
                body: args.emergencyContactDetails,
            });
        },
        onError: () => {
            notify('error', intl('createEmergencyContactError'));
        },
        onSuccess: () => {
            notify('success', intl('createEmergencyContactSuccess'));
            onCreateEmergencyContactSuccess?.();

            if (medicalProfileId) {
                onProfileContactDetailsUpdate.resetCache({ medicalProfileId })();
            }
        },
    });

    return {
        profile: profileQuery.data || null,
        isLoadingProfile: profileQuery.isLoading,
        updateContact: contactDetailsMutation.mutate,
        isUpdatingContact: contactDetailsMutation.isPending,
        createEmergencyContact: createEmergencyContact.mutateAsync,
        isCreatingEmergencyContact: createEmergencyContact.isPending,
        deleteEmergencyContact: deleteEmergencyContactMutation.mutateAsync,
        isDeletingEmergencyContact: deleteEmergencyContactMutation.isPending,
    };
}
